import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  cardAddressSchema,
  cardRelationCreateBodySchema,
  relatedCardListSchema,
  type CardRelationCreateBody,
  type CardAddress,
  type RelatedCardList,
} from '@molt/shared';

import { del, get, post } from './api';
import { queryKeys } from './query-keys';

export interface CreateCardRelationVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly body: CardRelationCreateBody;
}

export interface DeleteCardRelationVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly toCardId: string;
}

export function getRelatedCards(cardId: string): Promise<RelatedCardList> {
  return get(`/cards/${encodeURIComponent(cardId)}/relations`, relatedCardListSchema);
}

export function getCardAddressByShortLink(shortLink: string): Promise<CardAddress> {
  return get(`/cards/by-short-link/${encodeURIComponent(shortLink)}`, cardAddressSchema);
}

export function createCardRelation(
  cardId: string,
  body: CardRelationCreateBody,
): Promise<RelatedCardList> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/relations`,
    relatedCardListSchema,
    cardRelationCreateBodySchema.parse(body),
  );
}

export function deleteCardRelation(
  cardId: string,
  toCardId: string,
): Promise<RelatedCardList> {
  return del(
    `/cards/${encodeURIComponent(cardId)}/relations/${encodeURIComponent(toCardId)}`,
    relatedCardListSchema,
  );
}

export function relatedCardsQueryOptions(cardId: string) {
  return queryOptions({
    queryKey: queryKeys.cards.relations(cardId),
    queryFn: () => getRelatedCards(cardId),
  });
}

export function useCreateCardRelation(): UseMutationResult<
  RelatedCardList,
  unknown,
  CreateCardRelationVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, body }) => createCardRelation(cardId, body),
    onSuccess: (relations, variables) => {
      queryClient.setQueryData(queryKeys.cards.relations(variables.cardId), relations);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.activity(variables.cardId, 'all'),
      });
    },
  });
}

export function useDeleteCardRelation(): UseMutationResult<
  RelatedCardList,
  unknown,
  DeleteCardRelationVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, toCardId }) => deleteCardRelation(cardId, toCardId),
    onSuccess: (relations, variables) => {
      queryClient.setQueryData(queryKeys.cards.relations(variables.cardId), relations);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.activity(variables.cardId, 'all'),
      });
    },
  });
}
